import { DropdownEditor, Field, TextAreaEditor } from '@approvalmax/ui';
import { Box, Flex } from '@approvalmax/ui/src/components';
import { constants, statics } from 'modules/common';
import { domain, State } from 'modules/data';
import {
    changeExchangeRateParams,
    changeQbooksBillingAddress,
    changeQbooksCustomer,
    ExchangeRateParams,
    showQBooksCustomerCreationPopup,
    updateQBooksLineItemsWithCurrencyExchangeRate,
} from 'pages/requestForm/actions';
import CurrencyExchangeRateInput from 'pages/requestForm/components/CurrencyExchangeRateInput/CurrencyExchangeRateInput';
import { ExchangeRateWithUpdate } from 'pages/requestForm/components/ExchangeRateWithUpdate/ExchangeRateWithUpdate';
import { SectionContainer } from 'pages/requestForm/components/SectionContainer';
import { getRequest, getShowValidationErrors } from 'pages/requestForm/selectors/pageSelectors';
import { getCanCreateContact, getIsExchangeRateInputAvailable } from 'pages/requestForm/selectors/requestSelectors';
import { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CurrencyDisclaimerLegacy } from '../../../../../components/(legacy)/CurrencyDisclaimerLegacy/CurrencyDisclaimerLegacy';
import { QBooksInvoiceCustomersDataProvider } from '../../../dataProviders/QBooksInvoiceCustomersDataProvider/QBooksInvoiceCustomersDataProvider';
import { QBooksCounterpartyDetails } from '../../../QBooksCounterpartyDetails/QBooksCounterpartyDetails';
import { messages } from './SalesInvoiceCustomerSection.messages';

export const SalesInvoiceCustomerSection = memo(() => {
    const dispatch = useDispatch();

    const showErrors = useSelector(getShowValidationErrors);
    const request = useSelector(getRequest<domain.QBooksInvoiceRequest>);
    const { companyCurrency, currency: requestCurrency, currencyExchangeRate: exchangeRateValue, details } = request;
    const { customer, billingAddress } = details;

    const isExchangeRateInputAvailable = getIsExchangeRateInputAvailable(request);

    const canCreateContact = useSelector((state: State) => getCanCreateContact(state, request));

    const onCreateCustomer = () => {
        dispatch(showQBooksCustomerCreationPopup());

        return Promise.resolve(null);
    };

    const handleBillingAddressChange = useCallback(
        (value: string) => {
            dispatch(changeQbooksBillingAddress(value));
        },
        [dispatch]
    );

    const onExchangeRateChange = useCallback(
        (params: ExchangeRateParams) => {
            dispatch(changeExchangeRateParams(params));
            dispatch(updateQBooksLineItemsWithCurrencyExchangeRate(params.exchangeRate ?? null));
        },
        [dispatch]
    );

    const handleManualChangeExchangeRate = useCallback(
        (value?: number) => {
            onExchangeRateChange({
                exchangeRate: value || null,
                exchangeRateSource: value ? domain.ExchangeRateSource.Manual : domain.ExchangeRateSource.None,
                exchangeRateDate: null,
            });
        },
        [onExchangeRateChange]
    );

    useEffect(() => {
        if (customer?.currency?.id === companyCurrency) {
            onExchangeRateChange({
                exchangeRate: null,
                exchangeRateSource: null,
                exchangeRateDate: null,
            });
        }
    }, [companyCurrency, customer?.currency?.id, onExchangeRateChange]);

    const handleQbooksCustomerChange = useCallback(
        (customer: domain.QBooksContact) => dispatch(changeQbooksCustomer(customer)),
        [dispatch]
    );

    return (
        <SectionContainer title={messages.sectionTitle}>
            <Flex container direction='column' spacing='8 0'>
                <Flex container spacing='20'>
                    <Flex size={12} block direction='column' spacing='8'>
                        <Box width='100%'>
                            <Field required title={messages.customerFieldTitle}>
                                <QBooksInvoiceCustomersDataProvider>
                                    <DropdownEditor
                                        invalid={showErrors && !customer}
                                        value={customer}
                                        singleLinePerItem={false}
                                        onChange={handleQbooksCustomerChange}
                                        placeholder={messages.customerPlaceholder}
                                        canCreateItemFn={() => canCreateContact}
                                        createItemDisplayTextFn={() => messages.createCustomer}
                                        onCreateItem={onCreateCustomer}
                                    />
                                </QBooksInvoiceCustomersDataProvider>
                            </Field>
                        </Box>

                        <QBooksCounterpartyDetails counterparty={customer} hideAddress />

                        {isExchangeRateInputAvailable && (
                            <CurrencyExchangeRateInput
                                selectedCurrency={
                                    customer?.currency || statics.currency.findCurrencyByCode(requestCurrency) || null
                                }
                                defaultCurrency={statics.currency.findCurrencyByCode(companyCurrency) || null}
                                exchangeRateValue={
                                    request.exchangeRateSource === domain.ExchangeRateSource.Manual
                                        ? exchangeRateValue
                                        : null
                                }
                                precision={7}
                                onExchangeRateChange={handleManualChangeExchangeRate}
                            />
                        )}

                        {request.exchangeRateSource === domain.ExchangeRateSource.Manual ? (
                            <CurrencyDisclaimerLegacy />
                        ) : (
                            <ExchangeRateWithUpdate
                                onChange={onExchangeRateChange}
                                requestId={request.id}
                                companyId={request.companyId}
                                date={request.details.date}
                                currency={request.currency}
                                companyCurrency={request.companyCurrency}
                            />
                        )}
                    </Flex>

                    <Flex size={6} block>
                        <Field title={messages.billingAddressFieldTitle}>
                            <TextAreaEditor
                                minHeight={4}
                                maxHeight={12}
                                maxLength={constants.qBooksConstants.MAILING_ADDRESS_MAX_LENGTH}
                                value={billingAddress}
                                onChange={handleBillingAddressChange}
                                placeholder={messages.billingAddressPlaceholder}
                                changeOnBlur
                            />
                        </Field>
                    </Flex>
                </Flex>
            </Flex>
        </SectionContainer>
    );
});

SalesInvoiceCustomerSection.displayName = 'SalesInvoiceCustomerSection';
